import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastWarning } from 'utils/toast';
import { encryptURL, findOnArray } from 'utils/functions';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { NotasContext } from 'contexts/NotasContext';
import { GridContext } from 'contexts/GridContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { useModal, useDialog } from 'components/Modals';
import ImportaXMLModal from 'components/Modals/ImportaXMLModal';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import columnOverrides from './columnOverrides';
import getNotasFilterFields from './filterFields';
import ImpExpModal from './Modals/ImpExpModal';
import ExpedicaoModal from './Modals/ExpedicaoModal';
import TransmissãoModal from './Modals/TransmissãoModal';
import InutilizaçãoModal from './Modals/InutilizaçãoModal';
import InutilizadosModal from './Modals/InutilizadosModal';

const Notas = () => {
  const rotina = 'Notas';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { openDialog, closeDialog } = useDialog();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const {
    notas,
    getNotas,
    exportXML,
    getLoading,
    postLoading,
    exportXMLAll,
    postAllExpedicao,
  } = useContext(NotasContext);
  const { postDocumentos } = useContext(DocumentosContext);

  const filterFields = useMemo(() => getNotasFilterFields(drops), [drops]);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();
    closeDialog();

    getNotas({ ...props, rotina, filter });
  };

  const impExpAction = ({ value, selected }) => {
    switch (value) {
      case '1':
        if (Boolean(selected?.length)) {
          postDocumentos({
            data: selected?.map((d) => ({
              Emitir: { id: d?.id, tpamb: 1, simplificado: true },
            })),
            cb: loadGrid,
          });
        } else {
          toastWarning('Selecione ao menos um item');
        }
        break;
      case '2':
        if (Boolean(selected?.length)) {
          navigate('/app/Notas/Etiquetas', {
            state: { documentos: selected },
          });
        } else {
          toastWarning('Selecione ao menos um item');
        }
        break;
      case '3':
        if (Boolean(selected?.length)) {
          exportXML({
            data: { notas: selected?.map((s) => s?.id), danfe: false },
            cb: loadGrid,
          });
        } else {
          toastWarning('Selecione ao menos um item');
        }
        break;
      case '4':
        if (Boolean(selected?.length)) {
          exportXML({
            data: { notas: selected?.map((s) => s?.id), danfe: true },
            cb: loadGrid,
          });
        } else {
          toastWarning('Selecione ao menos um item');
        }
        break;
      case '5':
        exportXMLAll({
          data: { rotina, filter: getValues(), danfe: false },
          cb: loadGrid,
        });
        break;
      case '6':
        exportXMLAll({
          data: { rotina, filter: getValues(), danfe: true },
          cb: loadGrid,
        });
        break;
      case '7':
        setTimeout(() => openModal(<InutilizadosModal />), 500);
        break;

      default:
        break;
    }
  };

  const toolbarActions = [
    {
      name: 'Adicionar Nota',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Notas/Gerar/NF'),
    },
    {
      name: 'Adicionar Fatura',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Notas/Gerar/Fatura'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: notas?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: notas?.order }),
    },
    // {
    //   name: 'Exportar XML',
    //   icon: 'file_download',
    //   action: (selected) => {
    //     if (!Boolean(selected?.length)) {
    //       exportXMLAll({
    //         data: { rotina, filter: getValues(), danfe: false },
    //         cb: loadGrid,
    //       });
    //     } else {
    //       exportXML({
    //         data: { notas: selected?.map((s) => s?.id), danfe: false },
    //         cb: loadGrid,
    //       });
    //     }
    //   },
    // },
    {
      name: 'Impressões / Exportações',
      icon: 'print',
      action: (selected) =>
        openModal(<ImpExpModal onSubmit={impExpAction} selected={selected} />),
    },
    // {
    //   name: 'Exportar XML Inutilizados',
    //   icon: 'file_download',
    //   action: () => openModal(<InutilizadosModal />),
    // },
    {
      name: 'Importar XML',
      icon: 'file_upload',
      action: () =>
        openDialog(
          <ImportaXMLModal callback={loadGrid} />,
          'Importação de Arquivos XML'
        ),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={notas?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Transmitir / Registrar Notas',
      icon: 'send',
      action: (selected) => {
        const notas = selected?.filter((f) =>
          Boolean(f?.status !== 'CONCLUIDO')
        );
        if (!Boolean(notas?.length)) {
          return toastWarning('Selecione apenas documentos não concluídos');
        } else {
          openModal(<TransmissãoModal selected={notas} callback={loadGrid} />);
        }
      },
    },
    {
      name: 'Inutilizar Numeração',
      icon: 'block',
      action: () => openModal(<InutilizaçãoModal callback={loadGrid} />),
    },
    // {
    //   name: 'Etiquetas Destinatário',
    //   icon: 'print',
    //   action: (documentos) => {
    //     if (Boolean(documentos?.length)) {
    //       navigate('/app/Notas/Etiquetas', { state: { documentos } });
    //     } else {
    //       toastWarning('Selecione ao menos um item');
    //     }
    //   },
    // },
    // {
    //   name: 'DANFE Simplificada',
    //   icon: 'print',
    //   action: (documentos) => {
    //     if (Boolean(documentos?.length)) {
    //       postDocumentos({
    //         data: documentos?.map((d) => ({
    //           Emitir: { id: d?.id, tpamb: 1, simplificado: true },
    //         })),
    //         cb: loadGrid,
    //       });
    //     } else {
    //       toastWarning('Selecione ao menos um item');
    //     }
    //   },
    // },
    {
      name: 'Gerar MDFE',
      icon: 'post_add',
      action: (documentos) => {
        if (Boolean(documentos?.length)) {
          const check = Boolean(
            documentos?.every(
              (s) => s?.status === 'CONCLUIDO' && s?.modelo === 'NFe'
            )
          );
          if (check) {
            navigate('/app/Notas/Gerar/MDFE', { state: { documentos } });
          } else {
            toastWarning(
              'Selecione somente Notas Fiscais eletrônicas concluídas'
            );
          }
        } else {
          toastWarning('Selecione ao menos um item');
        }
      },
    },
    {
      name: 'Liberar Expedição',
      icon: 'local_shipping',
      action: (documentos) => {
        const sts_exp = findOnArray(13, drops?.StatusDocumento, 'status');
        if (!Boolean(documentos?.length)) {
          if (!Boolean(notas?.data?.length)) {
            return toastWarning('Não existem documentos para expedição');
          }
          if (notas?.data?.some((s) => s?.status !== sts_exp)) {
            return toastWarning(`Selecione apenas documentos ${sts_exp}`);
          }
          if (
            notas?.data?.some((s) => s?.marketplace_logistica === 'Fulfillment')
          ) {
            return toastWarning('Não é possível expedir documentos do Full');
          }
          return openModal(
            <ExpedicaoModal
              onSubmit={() =>
                postAllExpedicao({
                  data: { rotina, filter: getValues() },
                  cb: loadGrid,
                })
              }
            />
          );
        }
        if (documentos?.some((s) => s?.status !== sts_exp)) {
          return toastWarning(`Selecione apenas documentos ${sts_exp}`);
        }
        if (
          documentos?.some((s) => s?.marketplace_logistica === 'Fulfillment')
        ) {
          return toastWarning('Não é possível expedir documentos do Full');
        }
        if (
          documentos?.some(
            (a) => a?.aprovacao === 'NAO' || a?.aprovacao === 'PENDENTE'
          )
        ) {
          return toastWarning('Selecione apenas documentos aprovados');
        }
        if (
          !documentos?.every(
            (s) =>
              ([10, 25]?.includes(s?.especie_id) && s?.status === 'PENDENTE') ||
              (s?.especie_id === 10 && s?.status === 'CONCLUIDO')
          )
        ) {
          return toastWarning('Documentos inválidos para expedição');
        }
        if (
          ['WEMOV', 'INCUBADORA_E']?.some((a) => user?.integracao?.includes(a))
        ) {
          const data = documentos?.map((d) => ({
            Registros: [{ documento_id: d?.id, status_id: 13 }],
          }));
          postDocumentos({ data, cb: loadGrid });
        } else {
          const hash = encryptURL(documentos?.map((d) => d?.id));
          navigate(`/app/Notas/Expedicao/${hash}`);
        }
      },
    },
  ];

  // const aprovacao = {
  //   PENDENTE: {
  //     name: 'Aprovação necessária',
  //     icon: 'priority_high',
  //     color: 'secondary.main',
  //   },
  //   SIM: { name: 'Aprovado', icon: 'check', color: 'primary.main' },
  //   NAO: { name: 'Reprovado', icon: 'close', color: 'secondary.main' },
  // };

  const rowActions = [
    {
      name: 'Aprovação necessária',
      icon: 'priority_high',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'PENDENTE',
    },
    {
      name: 'Aprovado',
      icon: 'check',
      color: 'primary.main',
      show: ({ row }) => row?.aprovacao === 'SIM',
    },
    {
      name: ' Reprovado',
      icon: 'close',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'NAO',
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} colunas={notas?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={notas}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading || postLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default Notas;
